import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Reveal, Overline, SectionHeading, useCollection } from "@/components/common";
import PageHero from "@/components/PageHero";
import { getSiteContent, getContent } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";

export default function About() {
  const [bio, setBio] = useState(null);
  const [phil, setPhil] = useState(null);
  const [vision, setVision] = useState(null);
  const [lead, setLead] = useState(null);
  const { data: timeline } = useCollection(() => getContent("timeline"), []);

  useEffect(() => {
    getSiteContent("biography").then(setBio).catch(() => {});
    getSiteContent("philosophy").then(setPhil).catch(() => {});
    getSiteContent("vision").then(setVision).catch(() => {});
    getSiteContent("leadership_summary").then(setLead).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <PageHero label="About" title="Dr. Bhawna Sirohi, MBBS, FRCP"
        intro="Medical Oncologist, researcher, educator and cancer advocate with over three decades of experience across India and the United Kingdom."
        image="https://images.unsplash.com/photo-1496865534669-25ec2a3a0fd3?crop=entropy&cs=srgb&fm=jpg" />

      <section className="bg-white py-20">
        <Container>
          <div className="grid gap-14 md:grid-cols-12">
            <div className="md:col-span-8">
              <Overline className="text-gold-dark">Biography</Overline>
              <div className="mt-5 space-y-5 text-base leading-relaxed text-softgrey-600">
                {(bio?.text || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="sticky top-28 rounded-lg border border-slate-200 bg-softgrey-100 p-7">
                <Overline className="text-gold-dark">Current Role</Overline>
                <p className="mt-3 font-serif text-xl text-navy">Medical Director & Consultant Medical Oncologist</p>
                <p className="mt-1 text-sm text-softgrey-600">Vedanta Medical Research Foundation – Balco Medical Centre, Raipur</p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {[["FRCP", "London, 2015"], ["h-index", "25"], ["Countries", "India & UK"], ["Since", "1991"]].map(([k, v]) => (
                    <div key={k}><div className="font-serif text-2xl text-navy">{v}</div><div className="text-xs text-softgrey-600">{k}</div></div>
                  ))}
                </div>
                <Link to="/awards" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark">Awards & Recognition <ArrowRight size={14} /></Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {phil?.points && (
        <section className="bg-softgrey-100 py-20">
          <Container>
            <SectionHeading label="Philosophy" title="An approach rooted in compassion and evidence" />
            <div className="grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
              {phil.points.map((p, i) => (
                <Reveal key={i} delay={(i % 3) * 0.06}>
                  <div className="h-full bg-white p-8">
                    <div className="overline text-gold-dark">0{i + 1}</div>
                    <h3 className="mt-3 font-serif text-2xl text-navy">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-softgrey-600">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      <section id="timeline" className="bg-white py-20 scroll-mt-24">
        <Container>
          <SectionHeading label="Professional Journey" title="A career across India and the UK" />
          <div className="relative border-l border-slate-200 pl-8 md:pl-12">
            {timeline.map((t, i) => (
              <Reveal key={t.id} delay={0.03 * (i % 4)}>
                <div className="relative pb-12 last:pb-0">
                  <span className="absolute -left-[41px] top-1.5 h-3 w-3 rounded-full border-2 border-gold bg-white md:-left-[57px]" />
                  <div className="overline text-gold-dark">{t.year}</div>
                  <h3 className="mt-2 font-serif text-2xl text-navy">{t.title}</h3>
                  <p className="mt-1 text-sm font-medium text-navy/70">{t.org}</p>
                  {t.location && <p className="text-sm text-softgrey-600">{t.location}</p>}
                  {t.description && <p className="mt-2 text-sm text-softgrey-600">{t.description}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {vision?.points && (
        <section className="bg-navy py-20 text-white">
          <Container>
            <SectionHeading light label="Vision" title="Shaping the future of cancer care" />
            <div className="grid gap-6 md:grid-cols-2">
              {vision.points.map((v, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <div className="flex gap-4 rounded-lg border border-white/10 p-6">
                    <span className="font-serif text-3xl text-gold">0{i + 1}</span>
                    <p className="text-sm leading-relaxed text-slate-300">{v}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            {lead?.text && (
              <div className="mt-12 rounded-lg border border-white/10 bg-white/5 p-8">
                <Overline className="text-gold">Leadership</Overline>
                <p className="mt-3 text-base leading-relaxed text-slate-300">{lead.text}</p>
              </div>
            )}
          </Container>
        </section>
      )}
    </div>
  );
}
